// Tier 3 (custom): antd has no chart component. Colours come from the antd theme via
// useToken(). Catalogue: .claude/skills/design-to-component/catalogue.md ("Histogram").
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { theme } from 'antd';

import { limitDecimal } from '@utils/helpers';
import { buildHistogramBuckets } from './histogramUtils';

const { useToken } = theme;

/**
 * Compact distribution chart for a single filter property — mirrors the shape
 * used for model-based detection filtering (Chart.jsx): a filled line chart
 * with a point per bucket and a hover tooltip showing that bucket's value
 * range and detection count. No axis labels — the min/max range is already
 * shown by the slider beneath it.
 *
 * Points are plotted on a real linear x-axis pinned to [min, max] (rather than
 * a categorical axis), with two invisible anchor points added exactly at min
 * and max. Without those anchors the visible line only spans between the
 * first and last bucket *midpoints* — noticeably short of the actual min/max
 * (e.g. a 10-bucket split of 25–90 has its first point at ~28, not 25) — which
 * makes the chart look like it's hiding part of the available range even
 * though the underlying bucket data already covers it fully.
 *
 * Renders nothing when there isn't enough data to build a meaningful chart —
 * callers should gate visibility (e.g. a toggle button) rather than relying on
 * this to always render.
 *
 * @param {{
 *   values: Array<number>,
 *   min: number,
 *   max: number,
 *   unit?: string,
 *   conversionFactor?: number,
 *   height?: number,
 * }} props
 */
const PropertyHistogram = ({ values, min, max, unit, conversionFactor = 1, height = 64 }) => {
	const { token } = useToken();

	const buckets = useMemo(() => buildHistogramBuckets(values, min, max), [values, min, max]);

	if (!buckets || !values?.length) return null;

	const displayValue = v => {
		const converted = limitDecimal(v * conversionFactor, 2);
		return unit ? `${converted}${unit === '%' ? '' : ' '}${unit}` : String(converted);
	};

	const midpoints = buckets.map(({ min: bMin, max: bMax }) => (bMin + bMax) / 2);
	const counts = buckets.map(({ count }) => count);

	// Real numeric x-values: an invisible leading anchor at `min`, the bucket
	// midpoints, then an invisible trailing anchor at `max` — so the line
	// visually spans the full available range regardless of bucket width.
	const xValues = [min, ...midpoints, max];
	const yValues = [counts[0], ...counts, counts[counts.length - 1]];
	const pointStyles = [0, ...counts.map(() => 3), 0];
	const pointHoverStyles = [0, ...counts.map(() => 5), 0];
	const pointHitStyles = [0, ...counts.map(() => 10), 0];

	// Tooltip title per point — anchors show the exact min/max instead of a
	// bucket range, since they don't represent their own bucket.
	const tooltipLabels = [
		displayValue(min),
		...buckets.map(({ min: bMin, max: bMax }) => `~${displayValue((bMin + bMax) / 2)}`),
		displayValue(max),
	];

	const data = {
		datasets: [
			{
				label: 'Count',
				data: xValues.map((x, i) => ({ x, y: yValues[i] })),
				fill: true,
				backgroundColor: `${token.colorPrimary}22`,
				borderColor: token.colorPrimary,
				tension: 0.1,
				borderWidth: 1,
				pointRadius: pointStyles,
				pointHoverRadius: pointHoverStyles,
				pointHitRadius: pointHitStyles,
				pointBackgroundColor: token.colorPrimary,
			},
		],
	};

	const chartOptions = {
		maintainAspectRatio: false,
		animation: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					title: items => tooltipLabels[items[0].dataIndex],
					label: item => `${item.parsed.y} detection${item.parsed.y === 1 ? '' : 's'}`,
				},
			},
		},
		scales: {
			x: {
				type: 'linear',
				min,
				max,
				ticks: { display: false },
				grid: { display: false },
				border: { display: false },
			},
			y: { ticks: { display: false }, grid: { display: false }, border: { display: false } },
		},
	};

	return (
		<div style={{ height, width: '100%' }}>
			<Line options={chartOptions} data={data} />
		</div>
	);
};

export default PropertyHistogram;
